"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUTH0_COMPLIANCE_STATUS_CLAIM = exports.ComplianceStatusClaimSchema = exports.RecordAcceptanceOutputSchema = exports.RecordAcceptanceInputSchema = exports.CheckDocumentComplianceOutputSchema = exports.CheckDocumentComplianceOrganizationPayloadSchema = exports.CheckDocumentComplianceInputSchema = exports.OrganizationDocumentStatusSchema = exports.DocumentComplianceStatusSchema = exports.OrganizationComplianceStateSchema = exports.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS = exports.DocumentRegistrySchema = exports.DocumentTypeEntrySchema = exports.DocumentVariantEntrySchema = exports.DocumentVersionEntrySchema = exports.DocumentVersionIdSchema = exports.DocumentVariantIdSchema = exports.DocumentTypeSchema = void 0;
exports.buildDocumentVersionId = buildDocumentVersionId;
exports.parseDocumentVersionId = parseDocumentVersionId;
exports.getCurrentDocumentVersion = getCurrentDocumentVersion;
exports.isVersionValidForWrite = isVersionValidForWrite;
const zod_1 = require("zod");
exports.DocumentTypeSchema = zod_1.z.enum(['tos', 'privacyPolicy']);
exports.DocumentVariantIdSchema = zod_1.z
    .string()
    .regex(/^(exempt|[a-z][a-z0-9_]{1,63})$/)
    .max(255);
exports.DocumentVersionIdSchema = zod_1.z
    .string()
    .regex(/^(tos|privacyPolicy):[a-z][a-z0-9_]{1,63}:\d{4}-\d{2}-\d{2}-(v\d+|draft)$/, {
    message: 'DocumentVersionId must be `{type}:{variant}:{version}` where version is `YYYY-MM-DD-vN` or `YYYY-MM-DD-draft` (e.g., tos:canada:2026-04-15-v1 or tos:canada:2026-04-27-draft)',
})
    .max(255);
function buildDocumentVersionId(type, variant, version) {
    return `${type}:${variant}:${version}`;
}
function parseDocumentVersionId(id) {
    const parts = id.split(':');
    if (parts.length !== 3) {
        throw new Error(`Invalid DocumentVersionId '${id}': expected 3 colon-separated parts`);
    }
    const [typeRaw, variant, version] = parts;
    if (typeRaw !== 'tos' && typeRaw !== 'privacyPolicy') {
        throw new Error(`Invalid DocumentVersionId '${id}': unknown document type '${typeRaw}'`);
    }
    return { type: typeRaw, variant, version };
}
exports.DocumentVersionEntrySchema = zod_1.z.object({
    id: exports.DocumentVersionIdSchema,
    version: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}-(v\d+|draft)$/),
    effectiveAt: zod_1.z.string().datetime(),
    publishedAt: zod_1.z.string().datetime(),
    url: zod_1.z.string().url(),
    pdfUrl: zod_1.z.string().url().optional(),
    webUrl: zod_1.z.string().url().optional(),
    mdChecksum: zod_1.z.string().regex(/^sha256:[a-f0-9]{64}$/),
    pdfChecksum: zod_1.z.string().regex(/^sha256:[a-f0-9]{64}$/),
    gracePeriodDays: zod_1.z.number().int().min(0),
    changelog: zod_1.z.string().optional(),
});
exports.DocumentVariantEntrySchema = zod_1.z.object({
    displayName: zod_1.z.string().min(1),
    versions: zod_1.z.array(exports.DocumentVersionEntrySchema).min(1),
});
exports.DocumentTypeEntrySchema = zod_1.z
    .object({
    displayName: zod_1.z.string().min(1),
    defaultVariant: exports.DocumentVariantIdSchema,
    variants: zod_1.z.record(zod_1.z.string().regex(/^[a-z][a-z0-9_]{1,63}$/), exports.DocumentVariantEntrySchema),
})
    .refine((v) => v.variants[v.defaultVariant] !== undefined, {
    message: '`defaultVariant` must reference a variant listed in `variants`',
});
exports.DocumentRegistrySchema = zod_1.z.object({
    schemaVersion: zod_1.z.literal('1'),
    updatedAt: zod_1.z.string().datetime(),
    documents: zod_1.z.object({
        tos: exports.DocumentTypeEntrySchema,
        privacyPolicy: exports.DocumentTypeEntrySchema,
    }),
});
exports.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS = 60 * 60 * 1000;
function getCurrentDocumentVersion(variant, now = new Date()) {
    const nowMs = now.getTime();
    const effectiveEntries = variant.versions.filter((v) => new Date(v.effectiveAt).getTime() <= nowMs);
    if (effectiveEntries.length === 0) {
        return null;
    }
    return effectiveEntries.reduce((newest, candidate) => new Date(candidate.effectiveAt).getTime() > new Date(newest.effectiveAt).getTime() ? candidate : newest);
}
function isVersionValidForWrite(version, now = new Date()) {
    const effectiveMs = new Date(version.effectiveAt).getTime();
    return effectiveMs - exports.DOCUMENT_CLOCK_JITTER_TOLERANCE_MS <= now.getTime();
}
exports.OrganizationComplianceStateSchema = zod_1.z.object({
    applicableTosVariant: exports.DocumentVariantIdSchema,
    applicablePrivacyVariant: exports.DocumentVariantIdSchema,
    tosAcceptedId: exports.DocumentVersionIdSchema.nullable(),
    tosAcceptedDate: zod_1.z.string().datetime().nullable(),
    tosAcceptedByUserId: zod_1.z.string().nullable(),
    privacyAcceptedId: exports.DocumentVersionIdSchema.nullable(),
    privacyAcceptedDate: zod_1.z.string().datetime().nullable(),
    privacyAcceptedByUserId: zod_1.z.string().nullable(),
});
exports.DocumentComplianceStatusSchema = zod_1.z.enum([
    'compliant',
    'grace_period',
    'blocked',
]);
exports.OrganizationDocumentStatusSchema = zod_1.z.object({
    state: exports.DocumentComplianceStatusSchema,
    gracePeriodEnd: zod_1.z.string().datetime().nullable(),
    latestVersionId: exports.DocumentVersionIdSchema.nullable(),
    latestVersionUrl: zod_1.z.string().url().nullable(),
    latestVersionName: zod_1.z.string().nullable(),
    acceptedVersionId: exports.DocumentVersionIdSchema.nullable(),
});
exports.CheckDocumentComplianceInputSchema = zod_1.z.object({
    auth0UserId: zod_1.z.string().min(1),
    auth0OrganizationId: zod_1.z.string().optional(),
});
exports.CheckDocumentComplianceOrganizationPayloadSchema = zod_1.z.object({
    cadstromOrganizationId: zod_1.z.string(),
    displayName: zod_1.z.string(),
    role: zod_1.z.enum(['admin', 'member', 'trainee']),
    canAcceptTos: zod_1.z.boolean(),
    canAcceptPrivacy: zod_1.z.boolean(),
    compliance: exports.OrganizationComplianceStateSchema,
    status: zod_1.z.object({
        tos: exports.OrganizationDocumentStatusSchema,
        privacy: exports.OrganizationDocumentStatusSchema,
    }),
});
exports.CheckDocumentComplianceOutputSchema = zod_1.z.object({
    cadstromUserId: zod_1.z.string(),
    organization: exports.CheckDocumentComplianceOrganizationPayloadSchema.nullable(),
});
exports.RecordAcceptanceInputSchema = zod_1.z.object({
    auth0UserId: zod_1.z.string().min(1),
    cadstromOrganizationId: zod_1.z.string().min(1),
    tosAcceptedId: exports.DocumentVersionIdSchema.nullable().optional(),
    privacyAcceptedId: exports.DocumentVersionIdSchema.nullable().optional(),
});
exports.RecordAcceptanceOutputSchema = zod_1.z.object({
    cadstromOrganizationId: zod_1.z.string(),
    compliance: exports.OrganizationComplianceStateSchema,
});
exports.ComplianceStatusClaimSchema = zod_1.z.object({
    tos: exports.DocumentComplianceStatusSchema,
    privacy: exports.DocumentComplianceStatusSchema,
    tos_grace_period_end: zod_1.z.string().datetime().nullable(),
    privacy_grace_period_end: zod_1.z.string().datetime().nullable(),
    tos_latest_id: exports.DocumentVersionIdSchema.nullable(),
    privacy_latest_id: exports.DocumentVersionIdSchema.nullable(),
    tos_url: zod_1.z.string().url().nullable(),
    privacy_url: zod_1.z.string().url().nullable(),
});
exports.AUTH0_COMPLIANCE_STATUS_CLAIM = 'https://cadstrom.io/compliance_status';
//# sourceMappingURL=document-acceptance.types.js.map